import { ArrowRight, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import { cn } from "@/app/handlers/cn.utils";
import type { TierName } from "@/app/hooks/useSubscription";

import type { Allowance } from "../config/allowances";

interface FitCheckPaywallProps {
	kind: "fitcheck" | "tailor";
	allowance: Allowance;
	tier: TierName;
	// When present, a quiet "Not now" appears beside the upgrade action — used inside the tailor dialog.
	onDismiss?: () => void;
	className?: string;
}

const NOUN: Record<FitCheckPaywallProps["kind"], { one: string; many: string }> = {
	fitcheck: { one: "FitCheck", many: "FitChecks" },
	tailor: { one: "tailored resume", many: "tailored resumes" },
};

// What the next step up unlocks, phrased for the action the user just hit a wall on.
function upgradeLine(kind: FitCheckPaywallProps["kind"], tier: TierName): string {
	if (kind === "fitcheck") return "Any paid plan unlocks unlimited FitChecks.";
	if (tier === "BRONZE") return "Silver includes 5 a month, Gold 20, Platinum unlimited.";
	if (tier === "SILVER") return "Gold includes 20 a month, and Platinum is unlimited.";
	return "Platinum includes unlimited tailored resumes.";
}

function resetLine(kind: FitCheckPaywallProps["kind"], nextResetAt: Date | null): string | null {
	if (!nextResetAt) return null;
	const days = Math.ceil((nextResetAt.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
	const noun = NOUN[kind].one;
	if (days <= 0) return `A ${noun} frees up today.`;
	if (days === 1) return `A ${noun} frees up tomorrow.`;
	return `A ${noun} frees up in ${days} days.`;
}

export function FitCheckPaywall({ kind, allowance, tier, onDismiss, className }: FitCheckPaywallProps) {
	const navigate = useNavigate();
	const { limit } = allowance;
	const noun = limit === 1 ? NOUN[kind].one : NOUN[kind].many;
	const reset = resetLine(kind, allowance.nextResetAt);

	return (
		<div
			className={cn(
				"mx-auto max-w-md rounded-xl border border-gray-200 bg-white p-8 text-center motion-safe:animate-fc-rise",
				className,
			)}
		>
			<span className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-700 motion-safe:animate-fc-pop">
				<Lock className="h-5 w-5" />
			</span>

			<h3 className="font-plus-jakarta text-lg font-bold text-gray-900 [animation-delay:60ms] motion-safe:animate-fc-rise">
				You have used all {limit} {noun} this month
			</h3>
			<p className="mx-auto mt-2 max-w-sm text-sm text-gray-600 [animation-delay:120ms] motion-safe:animate-fc-rise">
				{upgradeLine(kind, tier)}
			</p>

			<div className="mx-auto mt-5 max-w-xs [animation-delay:180ms] motion-safe:animate-fc-rise">
				<div className="mb-1.5 flex items-center justify-between text-xs font-medium text-gray-600">
					<span>{noun} used</span>
					<span className="tabular-nums">
						{allowance.used} of {limit}
					</span>
				</div>
				<div className="h-2 overflow-hidden rounded-full bg-gray-100">
					<div className="h-full w-full rounded-full bg-gray-300" />
				</div>
			</div>

			<div className="mt-7 flex flex-col items-center gap-2 [animation-delay:240ms] motion-safe:animate-fc-rise">
				<Button onClick={() => navigate("/payments")} className="w-full max-w-xs">
					See upgrade options
					<ArrowRight className="ml-2 h-4 w-4" />
				</Button>
				{reset && <p className="text-xs text-gray-600">{reset}</p>}
				{onDismiss && (
					<Button variant="ghost" size="sm" onClick={onDismiss} className="text-gray-500">
						Not now
					</Button>
				)}
			</div>
		</div>
	);
}

export default FitCheckPaywall;
